<template>
  <v-container grid-list-xl>
    <v-layout wrap v-if="toolOrder">
      <!--  Button actions -->
      <v-flex xs12>
        <!-- back to orders -->
        <v-btn
          class="mr-4 mb-2"
          small
          text
          color="primary"
          to="/tools/manage/orders"
        >
          <v-icon small class="mr-1">chevron_left</v-icon>Orders
        </v-btn>

        <!-- print for packing -->
        <v-btn
          v-if="incomplete()"
          small
          outlined
          color="primary"
          class="mr-4 mb-2"
          :to="`/tools/manage/print-for-packing/${$route.params.orderNumber}`"
        >
          <v-icon small class="mr-2">print</v-icon>Print for packing
        </v-btn>

        <!-- fulfill order -->
        <v-dialog v-model="fulfillmentDialog" width="500">
          <template v-slot:activator="{ on }">
            <v-btn
              v-if="incomplete()"
              small
              color="primary"
              class="mr-4 mb-2"
              v-on="on"
            >
              <v-icon small class="mr-2">assignment_turned_in</v-icon>Fulfill
              order
            </v-btn>
          </template>

          <v-card class="pa-4">
            <v-card-title class="title lighten-2" primary-title
              >Add Optional Note</v-card-title
            >

            <v-card-text>
              <v-textarea
                outlined
                label="Note for foreman."
                class="mt-4"
                v-model="fulfillmentNote"
                autofocus
              ></v-textarea>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" text @click="fulfillmentDialog = false"
                >Cancel</v-btn
              >
              <v-btn color="primary" @click="fulfillOrder">
                <v-icon small class="mr-2">assignment_turned_in</v-icon>Fulfill
                order
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- add tools -->
        <v-dialog v-model="addToolDialog" width="800">
          <template v-slot:activator="{ on }">
            <v-btn
              v-if="!toolOrder.fulfilled"
              small
              color="secondary"
              class="mr-4 mb-2"
              v-on="on"
              @click="loadTools"
            >
              <v-icon small class="mr-2">build</v-icon>Add to Order
            </v-btn>
          </template>

          <v-card class="pa-4">
            <v-card-title class="title lighten-2" primary-title>
              Add Tool
              <v-spacer></v-spacer>
              <v-icon @click="addToolDialog = false">cancel</v-icon>
            </v-card-title>

            <v-card-text>
              <v-data-table
                :headers="toolHeaders"
                :items="toolList"
                class="mb-4 pb-4 elevation-1"
                :loading="loadingTools"
                loading-text="Loading tools... Please wait"
                :search="search"
              >
                <template v-slot:top>
                  <v-toolbar flat color="white" class="pt-1">
                    <v-text-field
                      v-model="search"
                      append-icon="search"
                      label="Filter by name or description"
                      hide-details
                      outlined
                      dense
                    ></v-text-field>
                  </v-toolbar>
                </template>
                <template v-slot:item.image="{ item }">
                  <div class="py-2">
                    <v-img :src="item.image" width="60" height="60" contain />
                  </div>
                </template>
                <template v-slot:item.title="{ item }">
                  <strong class="grey--text text--darken-3">{{
                    item.title
                  }}</strong>
                </template>
                <template v-slot:item.qty="{ item }">
                  <div class="pt-4">
                    <v-text-field
                      class="d-inline-block ml-2 centered-input qty"
                      name="qty"
                      v-model.number="item.qty"
                      style="width:60px"
                      :dense="true"
                      type="number"
                    ></v-text-field>
                  </div>
                </template>
                <template v-slot:item.other="{ item }">
                  <div class="pt-4">
                    <v-btn
                      color="primary"
                      text
                      outlined
                      small
                      :disabled="item.qty < 1"
                      @click="addTool(item)"
                      >Add tool</v-btn
                    >
                  </div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-dialog>

        <!-- cancel order -->
        <v-dialog v-model="cancelOrderDialog" width="600">
          <template v-slot:activator="{ on }">
            <v-btn
              v-if="!toolOrder.cancelled"
              dark
              small
              color="grey darken-2"
              class="mr-4 mb-2"
              v-on="on"
              @click="cancelOrderDialog = true"
            >
              <v-icon small class="mr-2">cancel</v-icon>Cancel Order
            </v-btn>
          </template>

          <v-card class="pa-4">
            <v-card-title class="title lighten-2" primary-title>
              Cancel Order
            </v-card-title>

            <v-card-text>
              Are you sure you want to cancel this entire order?
              <br />Tools on this order will not be counted towards reporting or
              tools on site.
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" text @click="cancelOrderDialog = false"
                >Cancel</v-btn
              >
              <v-btn color="primary" @click="cancelOrder">
                Cancel Order
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-flex>
      <!--  tool order details-->
      <v-flex xs12>
        <tool-order-details
          v-if="toolOrder"
          :toolOrder="toolOrder"
          :admin="true"
          :incomplete="incomplete"
          :isFirstOrder="isFirstOrder"
        />
      </v-flex>

      <!-- tool list -->
      <v-flex xs12>
        <v-card v-if="toolOrder">
          <v-simple-table>
            <thead>
              <tr>
                <th class="text-left" width="60"></th>
                <th class="text-left" width="300">Name</th>
                <th class="text-center" width="40">Requested</th>
                <th class="text-center" width="40">Fulfilled</th>
                <th class="text-center" width="40">Amount</th>
                <th>Delivery By</th>
                <th class="text-right" width="20"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="tool in toolOrder.tools" :key="tool.id">
                <!-- image -->
                <td class="px-3 py-2">
                  <v-img :src="tool.image" width="50" height="50" contain />
                </td>
                <!-- name and other meta info -->
                <td>
                  {{ tool.title }}
                  <v-chip
                    class="ml-1"
                    x-small
                    v-if="tool.isRental"
                    color="green lighten-4"
                  >
                    <v-icon class="mr-1" x-small>monetization_on</v-icon>Rented
                    on
                    <span v-if="tool.rentalDate.seconds">{{
                      tool.rentalDate.toDate() | moment("MMM Do YYYY")
                    }}</span>
                    <span v-else>{{
                      tool.rentalDate | moment("MMM Do YYYY")
                    }}</span>
                  </v-chip>
                  <v-chip
                    class="ml-1"
                    x-small
                    dark
                    v-show="tool.cancelled"
                    color="grey darkern-2"
                  >
                    Cancelled
                  </v-chip>
                  <v-chip
                    class="ml-1"
                    x-small
                    dark
                    v-show="tool.settled"
                    color="cyan lighten-2"
                  >
                    Settled
                  </v-chip>
                  <div
                    class="blue--text text--darken-3"
                    v-if="
                      fulfilledAmount(tool) < tool.qty &&
                        fulfilledAmount(tool) !== 0 &&
                        !tool.settled
                    "
                  >
                    <v-icon small color="blue darken-2" left
                      >assignment_turned_in</v-icon
                    >Partially fulfilled
                  </div>
                  <div v-if="tool.consumable">
                    <span class="caption secondary--text text--darken-4"
                      >Unit Price: ${{ tool.unitPrice.toFixed(2) }}</span
                    >
                  </div>
                  <div v-else>
                    <span class="caption green--text text--darken-4"
                      >Value: ${{ tool.unitPrice.toFixed(2) }}</span
                    >
                  </div>
                </td>
                <!-- requested -->
                <td class="text-center">{{ tool.qty }}</td>
                <!-- fulfilled -->
                <td class="text-center">{{ fulfilledAmount(tool) }}</td>
                <!-- how many to fulfill -->
                <td class="text-center">
                  <v-text-field
                    class="d-inline-block ml-2 centered-input fulfillment-input"
                    v-model.number="tool.amount"
                    style="width:100px"
                    dense
                    outlined
                    type="number"
                    v-if="
                      tool.qty > fulfilledAmount(tool) &&
                        !tool.cancelled &&
                        !tool.settled
                    "
                    :rules="[
                      (v) =>
                        v <= tool.qty - fulfilledAmount(tool) || 'Too Much',
                    ]"
                  ></v-text-field>
                  <span v-else
                    ><v-icon v-if="!tool.cancelled" color="green lighten-3"
                      >check_circle</v-icon
                    >
                    <v-icon v-else color="grey lighten-3"
                      >check_circle</v-icon
                    ></span
                  >
                </td>
                <!-- delivery by -->
                <td>
                  <div
                    v-if="
                      fulfilledAmount(tool) > 0 ||
                        tool.cancelled ||
                        tool.settled
                    "
                  >
                    {{ tool.deliveredBy }}
                  </div>
                  <div v-else>
                    <v-select
                      :items="[
                        'Alba Yard',
                        'GC Warehouse',
                        'Fieldman Lumber',
                        'Casa Building Supplies',
                        'PowerPak',
                        'Swing Stage Scaffolding',
                        'Colgate Scaffolding',
                        'All Fastners',
                        'Maine Service',
                        'Airweld',
                        'Durante',
                        'Other - 3rd Party',
                      ]"
                      v-model="tool.deliveredBy"
                      dense
                    ></v-select>
                  </div>
                </td>
                <!-- options -->
                <td>
                  <div v-if="fulfilledAmount(tool) !== tool.qty">
                    <v-menu bottom left>
                      <template v-slot:activator="{ on }">
                        <v-btn icon v-on="on">
                          <v-icon>mdi-dots-vertical</v-icon>
                        </v-btn>
                      </template>

                      <v-list>
                        <v-list-item
                          v-for="o in toolOptions"
                          :key="o.title"
                          @click="toolMenu(o, tool)"
                        >
                          <v-list-item-title>{{ o.title }}</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>
        <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
          {{ snackText }}
          <v-btn text @click="snack = false">Close</v-btn>
        </v-snackbar>
      </v-flex>

      <!-- order issues -->
      <v-flex xs12 v-if="toolOrder && toolOrder.issues">
        <h3 class="pa-3 title">
          Order Issues Reported
        </h3>

        <v-simple-table class="elevation-2">
          <thead>
            <tr>
              <th class="text-left">Issue description</th>
              <th class="text-left">Tools</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(issue, ix) in toolOrder.issues" :key="ix">
              <td class="py-4">
                <strong>{{ issue.issueNote }}</strong
                ><br /><span class="caption grey--text">{{
                  issue.createDateString
                }}</span>
                <div v-if="issue.notes && issue.notes.length">
                  <p class="ma-0 mt-2">NOTES FROM YARD</p>
                  <div
                    v-for="(issueNote, noteIx) in issue.notes"
                    :key="noteIx"
                    class="primary--text"
                  >
                    <v-icon small left color="primary lighten-2">note</v-icon
                    >{{ issueNote.note }}
                    <span class="caption grey--text"
                      ><em>
                        by {{ issueNote.noteBy }} on
                        {{ issueNote.createDateString }}</em
                      ></span
                    >
                  </div>
                </div>
                <div v-if="issue.resolved">
                  <v-icon small left color="blue">check_circle</v-icon
                  ><strong>RESOLVED</strong>: {{ issue.resolved.note }}
                  <span class="caption grey--text"
                    ><em>
                      by {{ issue.resolved.resolvedBy }} on
                      {{ issue.resolved.createDateString }}</em
                    ></span
                  >
                </div>
                <div class="mt-3 mb-2" v-if="!issue.resolved">
                  <v-textarea
                    name="name"
                    label="Add Note"
                    id="id"
                    v-model="issueNoteYard"
                    outlined
                    :single-line="true"
                    dense
                    hide-details
                    class="mb-2"
                    auto-grow
                    rows="2"
                  ></v-textarea>
                  <v-btn
                    color="primary"
                    class="mr-4"
                    small
                    text
                    outlined
                    @click="saveIssueNote(ix)"
                    ><v-icon small left>save</v-icon> Save Note</v-btn
                  >
                  <v-dialog v-model="resolveDialog" width="500">
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-if="!issue.resolved"
                        small
                        outlined
                        text
                        color="primary"
                        class="mr-2"
                        v-on="on"
                      >
                        <v-icon small left>check_circle</v-icon>
                        resolve
                      </v-btn>
                    </template>

                    <v-card class="pa-4">
                      <v-card-title class="title lighten-2" primary-title
                        >Resolve Issue<span class="caption"
                          >Reference any pickups/orders that may have been
                          created.</span
                        ></v-card-title
                      >

                      <v-card-text>
                        <v-textarea
                          outlined
                          label="Describe how the issue was resolved"
                          class="mt-4"
                          v-model="resolvedNote"
                          autofocus
                        ></v-textarea>
                      </v-card-text>

                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn
                          color="primary"
                          text
                          @click="resolveDialog = false"
                          >Cancel</v-btn
                        >
                        <v-btn color="primary" @click="resolveIssue(ix)">
                          Resolve
                        </v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>
                </div>
              </td>
              <td>
                {{ issue.toolsWithIssues.map((t) => t.title).join(", ") }}
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-flex>
    </v-layout>
  </v-container>
</template>
<script>
import fb from "@/main";
import { sortBy, sumBy } from "lodash";
import { mapState } from "vuex";
import ToolOrderDetails from "@/components/toolOrders/ToolOrderDetails";
import moment from "moment";

export default {
  components: { ToolOrderDetails },
  data() {
    return {
      toolOptions: [
        { title: "Rental Flag" },
        { title: "Cancel Item" },
        { title: "Settled" },
      ],
      search: "",
      fulfillmentDialog: false,
      addToolDialog: false,
      cancelOrderDialog: false,
      fulfillmentNote: "",
      snack: false,
      snackColor: "",
      snackText: "",
      loadingTools: false,
      toolHeaders: [
        {
          text: "",
          align: "left",
          sortable: false,
          value: "image",
          width: 60,
        },
        { text: "", value: "title", sortable: false },
        { text: "", value: "qty", sortable: false },
        {
          text: "",
          value: "other",
          width: 60,
          align: "right",
          sortable: false,
        },
      ],
      toolOrder: null,
      toolList: [],
      resolveDialog: false,
      resolvedNote: "",
      issueNoteYard: "",
      isFirstOrder: false,
    };
  },
  async created() {
    this.getOrder();
  },
  methods: {
    async fulfillOrder() {
      try {
        // generate fulfillment id
        const fulfillmentId = moment().unix();

        const fulfillment = {
          id: fulfillmentId,
          fulfilledBy: this.user,
          fulfillmentNote: this.fulfillmentNote,
        };
        this.toolOrder.fulfillments.push(fulfillment);

        const emailToolData = {
          fulfilledTools: [],
          unfulfilledTools: [],
        };
        this.toolOrder.tools.forEach((tool) => {
          if (tool.amount === "") tool.amount = 0;

          if (tool.amount > 0) {
            tool.fulfillments.push({
              id: fulfillmentId,
              qty: tool.amount,
            });
            emailToolData.fulfilledTools.push(tool);
          } else {
            tool.fulfilledAmount = this.fulfilledAmount(tool);
            emailToolData.unfulfilledTools.push(tool);
          }
        });

        // console.log("tools", this.toolOrder.tools);
        await fb.toolOrders_DB.doc(this.toolOrder.orderNumber).update({
          tools: this.toolOrder.tools,
          fulfillments: this.toolOrder.fulfillments,
          fulfilled: true,
          fulfillmentDate: fb.timestamp,
          fulfilledBy: this.user,
        });

        // send email
        await fb.db.collection("toolOrderEmails").add({
          type: "fulfillment",
          subject:
            this.toolOrder.fulfillments.length == 1 &&
            !emailToolData.unfulfilledTools.length
              ? `Your order #${this.toolOrder.orderNumber} has been fulfilled!`
              : `PARTIAL - Your order #${this.toolOrder.orderNumber} has been fulfilled!`,
          jobSite: this.toolOrder.jobSite,
          fulfillmentNote: this.fulfillmentNote,
          orderNumber: this.toolOrder.orderNumber,
          orderedBy: this.toolOrder.orderedBy,
          orderNote: this.toolOrder.notes,
          otherTools: emailToolData.unfulfilledTools.length ? true : false,
          emailToolData,
        });

        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Order fulfilled!";
        this.fulfillmentNote = "";
        this.fulfillmentDialog = false;
        this.getOrder();
      } catch (error) {
        console.log(error);
        alert("Error fulfilling order.");
      }
    },
    toolMenu(o, t) {
      if (o.title == "Rental Flag") {
        if (t.consumable) {
          alert("cannot set rental flag on consumable items.");
          return;
        }
        this.$set(t, "isRental", !t.isRental);
        if (t.isRental) {
          this.$set(t, "rentalDate", new Date());
        } else {
          this.$delete(t, "rentalDate");
        }
      } else if (o.title == "Cancel Item") {
        this.$set(t, "cancelled", !t.cancelled);
        this.updateOrder();
      } else if (o.title == "Settled") {
        this.$set(t, "settled", !t.settled);
        this.updateOrder();
      }
    },
    async loadTools() {
      if (this.tools.length) {
        this.tools.forEach((t) => {
          this.toolList.push(t);
        });
      } else if (!this.toolList.length) {
        const res = await fb.db.collection("tools").get();
        res.forEach((u) => {
          this.toolList.push({
            id: u.id,
            ...u.data(),
            qty: 0,
          });
        });
      }
    },
    async getOrder() {
      try {
        let toolOrder = await fb.toolOrders_DB
          .doc(this.$route.params.orderNumber)
          .get();
        toolOrder = toolOrder.data();
        // if no delivery by set it to alba yard
        toolOrder.tools.forEach((o) => {
          if (!o.deliveredBy) {
            o.deliveredBy = "Alba Yard";
          }
        });
        // move all non consumables to top
        toolOrder.tools = sortBy(
          toolOrder.tools,
          [
            (t) => {
              return !t.consumable;
            },
          ],
          ["id"]
        ).reverse();
        // set amount to fulfill to zero
        toolOrder.tools.forEach((t) => {
          if (!t.fulfillments) t.fulfillments = [];
          t.amount = 0;
        });
        // support previous flow
        if (!toolOrder.fulfillments) toolOrder.fulfillments = [];
        console.log(toolOrder);

        // Check if this is the first order for this job site
        await this.checkFirstOrder(toolOrder);

        // assign
        this.toolOrder = toolOrder;
      } catch (error) {
        alert("Error getting orders.");
        console.log(error);
      }
    },
    async checkFirstOrder(toolOrder) {
      try {
        // Query for any tool orders with the same job site that are older than this one
        const olderOrders = await fb.toolOrders_DB
          .where("jobSite.id", "==", toolOrder.jobSite.id)
          .where("orderDate", "<", toolOrder.orderDate)
          .limit(1)
          .get();

        this.isFirstOrder = olderOrders.empty;
      } catch (error) {
        console.log("Error checking first order:", error);
        this.isFirstOrder = false;
      }
    },
    clearQty(id) {
      this.toolList.forEach((t) => {
        if (t.id == id) {
          t.qty = 0;
        }
      });
    },
    async addTool(tool) {
      // check if tool is already in order
      let found = null;
      found = this.toolOrder.tools.find((t) => {
        return t.id == tool.id;
      });
      if (found) {
        this.snack = true;
        this.snackColor = "error";
        this.snackText = "This tool is already in the order!";
        this.clearQty(tool.id);
      } else {
        tool.fulfillments = [];
        this.toolOrder.tools.push(tool);
        this.updateOrder();
        this.clearQty(tool.id);
      }
    },
    async updateOrder() {
      try {
        await fb.toolOrders_DB.doc(this.toolOrder.orderNumber).update({
          toolIds: this.toolOrder.tools.map((t) => t.id),
          tools: this.toolOrder.tools,
        });

        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Order Updated!";
        this.getOrder();
      } catch (error) {
        console.log(error);
        alert("Error updating order.");
      }
    },
    async cancelOrder() {
      try {
        // Mark order as cancelled
        this.cancelOrderDialog = false;
        await fb.db
          .collection("toolOrders")
          .doc(this.toolOrder.orderNumber)
          .update({
            cancelled: true,
          });

        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Order Cancelled!";
        this.getOrder();
      } catch (error) {
        console.log(error);
        alert("Error cancelling order.");
      }
    },
    fulfilledAmount(tool) {
      if (!tool.fulfillments.length) {
        return tool.qtyFulfilled || 0;
      } else {
        return sumBy(tool.fulfillments, "qty");
      }
    },
    incomplete() {
      // cycle through all and determine if all items are fulfilled
      const found = this.toolOrder.tools.find((t) => {
        if (!t.cancelled) {
          return this.fulfilledAmount(t) < t.qty;
        }
      });
      return !!found;
    },
    async resolveIssue(ix) {
      const issues = [...this.toolOrder.issues];
      issues[ix].resolved = {
        note: this.resolvedNote,
        resolvedBy: `${this.user.firstName} ${this.user.lastName}`,
        createDateString: moment().format("MMM Do [at] h:mma"),
      };
      this.resolveDialog = false;
      try {
        await fb.toolOrders_DB
          .doc(this.toolOrder.orderNumber)
          .update({ issues });
        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Issue Note Added!";
        this.resolvedNote = "";
        this.getOrder();
      } catch (error) {
        console.log(error);
        alert("Error saving note.");
      }
    },
    async saveIssueNote(ix) {
      const issues = [...this.toolOrder.issues];
      issues[ix].notes = issues[ix].notes || [];
      issues[ix].notes.push({
        note: this.issueNoteYard,
        noteBy: `${this.user.firstName} ${this.user.lastName}`,
        createDateString: moment().format("MMM Do [at] h:mma"),
      });
      try {
        await fb.toolOrders_DB
          .doc(this.toolOrder.orderNumber)
          .update({ issues });
        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Issue Note Added!";
        this.issueNoteYard = "";
        this.getOrder();
      } catch (error) {
        console.log(error);
        alert("Error saving note.");
      }
    },
  },
  computed: {
    ...mapState(["user", "tools"]),
  },
};
</script>

<style scoped>
.fulfillment-input {
  margin-bottom: -20px;
}
</style>
